import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';

interface ServicesProps {
  t: any;
  isRTL: boolean;
}

const ServicesSection = styled.section`
  padding: 8rem 2rem;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="%23e2e8f0" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
    opacity: 0.3;
  }
`;

const Container = styled.div<{ $isRTL: boolean }>`
  max-width: 1400px;
  margin: 0 auto;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  position: relative;
  z-index: 1;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 6rem;
`;

const SectionTitle = styled(motion.h2)<{ $isRTL: boolean }>`
  font-size: 3.5rem;
  color: #1f2937;
  margin-bottom: 1.5rem;
  position: relative;
  font-weight: 700;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  background: linear-gradient(135deg, #0f172a 0%, #dc2626 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 5px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    border-radius: 3px;
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled(motion.h3)<{ $isRTL: boolean }>`
  font-size: 1.4rem;
  color: #6b7280;
  font-weight: 400;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  margin-top: 2rem;
`;

const ServicesGrid = styled.div<{ $isRTL: boolean }>`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2.5rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ServiceCard = styled(motion.div)<{ $isRTL: boolean }>`
  background: white;
  padding: 3rem 2.5rem;
  border-radius: 25px;
  text-align: center;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08);
  border: 2px solid transparent;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  cursor: ${props => props.onClick ? 'pointer' : 'default'};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-15px) scale(1.02);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
    border-color: #dc2626;
    
    &::before {
      transform: scaleX(1);
    }
  }
  
  &:hover .service-icon {
    transform: scale(1.1) rotate(5deg);
  }
`;

const ServiceIcon = styled.div`
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  border-radius: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  box-shadow: 0 10px 30px rgba(220, 38, 38, 0.3);
  transition: all 0.4s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    inset: -2px;
    background: linear-gradient(135deg, #dc2626, #b91c1c, #dc2626);
    border-radius: 25px;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.4s ease;
  }
  
  img {
    width: 50px;
    height: 50px;
    filter: brightness(0) invert(1);
    transition: all 0.3s ease;
  }
  
  &:hover::after {
    opacity: 1;
  }
`;

const ServiceTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  line-height: 1.3;
`;

const ServiceDescription = styled.p<{ $isRTL: boolean }>`
  color: #6b7280;
  line-height: 1.7;
  font-size: 1.1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  margin: 0;
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
`;

const FloatingShape = styled(motion.div)`
  position: absolute;
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(185, 28, 28, 0.1) 100%);
  border-radius: 50%;
  
  &:nth-child(1) {
    width: 200px;
    height: 200px;
    top: 10%;
    left: 5%;
  }
  
  &:nth-child(2) {
    width: 150px;
    height: 150px;
    top: 60%;
    right: 10%;
  }
  
  &:nth-child(3) {
    width: 100px;
    height: 100px;
    bottom: 20%;
    left: 15%;
  }
`;

const Services: React.FC<ServicesProps> = ({ t, isRTL }) => {
  const navigate = useNavigate();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const services = [
    { 
      title: t.service2, 
      description: t.service2Desc,
      icon: "/images/services/installation.svg"
    },
    { 
      title: t.service4, 
      description: t.service4Desc,
      icon: "/images/services/solaire.svg"
    },
    { 
      title: t.service1, 
      description: t.service1Desc,
      icon: "/images/services/monitoring.svg"
    },
  ];

  return (
    <ServicesSection id="services" ref={ref}>
      <FloatingElements>
        <FloatingShape
          animate={{
            y: [0, -20, 0],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <FloatingShape
          animate={{
            y: [0, 20, 0],
            rotate: [360, 180, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <FloatingShape
          animate={{
            y: [0, -15, 0],
            rotate: [0, -180, -360],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </FloatingElements>
      
      <Container $isRTL={isRTL}>
        <SectionHeader>
          <SectionTitle
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            {t.servicesTitle}
          </SectionTitle>
          <SectionSubtitle
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {t.servicesSubtitle}
          </SectionSubtitle>
        </SectionHeader>

        <ServicesGrid $isRTL={isRTL}>
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              $isRTL={isRTL}
              initial={{ opacity: 0, y: 60 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ 
                duration: 0.8, 
                delay: 0.5 + index * 0.2,
                type: "spring",
                stiffness: 100
              }}
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.3 }
              }}
              onClick={() => {
                if (service.title === t.service2) {
                  navigate('/installation-service');
                } else if (service.title === t.service4) {
                  navigate('/solaire');
                } else if (service.title === t.service1) {
                  navigate('/monitoring');
                }
              }}
            >
              <ServiceIcon className="service-icon">
                <img src={service.icon} alt={service.title} />
              </ServiceIcon>
              <ServiceTitle $isRTL={isRTL}>{service.title}</ServiceTitle>
              <ServiceDescription $isRTL={isRTL}>{service.description}</ServiceDescription>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </Container>
    </ServicesSection>
  );
};

export default Services;
